if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.dialog = this.querySelector('[role="dialog"]');
        this.viewport = this.querySelector('.lightbox__viewport');
        this.track = this.querySelector('.lightbox__track');
        this.slides = Array.from(this.querySelectorAll('.lightbox__slide'));
        this.prevBtn = this.querySelector('.lightbox__nav--prev');
        this.nextBtn = this.querySelector('.lightbox__nav--next');
        this.zoomInBtn = this.querySelector('.lightbox__zoom--in');
        this.zoomOutBtn = this.querySelector('.lightbox__zoom--out');
        this.currentEl = this.querySelector('.lightbox__current');

        this.index = 0;
        this.MIN_ZOOM = 1;
        this.MAX_ZOOM = 3;
        this.ZOOM_STEP = 0.5;
        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;

        // pointer/gesture state
        this.pointers = new Map();
        this.dragging = false;
        this.swiping = false;
        this.startX = 0;
        this.startY = 0;
        this.startPanX = 0;
        this.startPanY = 0;
        this.pinchStartDist = 0;
        this.pinchStartZoom = 1;
        this.movedDuringGesture = false;

        if (this.slides.length <= 1) this.classList.add('lightbox--single');

        this.bindEvents();
        this.update();
      }

      /* ----- open / close ----- */

      show(opener) {
        super.show(opener);
        const id = opener && opener.getAttribute('data-media-id');
        let idx = this.slides.findIndex((s) => s.dataset.mediaId === id);
        if (idx < 0) idx = 0;
        this.goTo(idx, false);
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      /* ----- slides (no loop: clamped + disabled at edges) ----- */

      goTo(index, animate = true) {
        this.index = Math.max(0, Math.min(index, this.slides.length - 1));
        this.resetZoom();
        if (!animate) this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${-this.index * 100}%)`;
        if (!animate) {
          // re-enable transition on next frame
          requestAnimationFrame(() => {
            this.track.style.transition = '';
          });
        }
        this.update();
      }

      next() {
        if (this.index < this.slides.length - 1) this.goTo(this.index + 1);
      }

      prev() {
        if (this.index > 0) this.goTo(this.index - 1);
      }

      currentSlide() {
        return this.slides[this.index];
      }

      currentImage() {
        const slide = this.currentSlide();
        if (!slide || slide.dataset.mediaType !== 'image') return null;
        return slide.querySelector('img');
      }

      update() {
        if (this.currentEl) this.currentEl.textContent = this.index + 1;
        if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
        // Zoom controls only make sense on image slides
        this.classList.toggle('lightbox--no-zoom', !this.currentImage());
        this.updateZoomButtons();
      }

      /* ----- zoom (no loop: clamped + disabled at min/max) ----- */

      zoomIn() {
        this.setZoom(this.zoom + this.ZOOM_STEP);
      }

      zoomOut() {
        this.setZoom(this.zoom - this.ZOOM_STEP);
      }

      setZoom(value) {
        const next = Math.max(this.MIN_ZOOM, Math.min(value, this.MAX_ZOOM));
        this.zoom = next;
        if (this.zoom === this.MIN_ZOOM) {
          this.panX = 0;
          this.panY = 0;
        }
        this.applyTransform();
        this.updateZoomButtons();
      }

      resetZoom() {
        this.zoom = this.MIN_ZOOM;
        this.panX = 0;
        this.panY = 0;
        this.slides.forEach((slide) => {
          const img = slide.querySelector('img');
          if (img) {
            img.style.transform = '';
            img.classList.remove('is-zoomed', 'is-grabbing');
          }
        });
        this.updateZoomButtons();
      }

      updateZoomButtons() {
        const isImage = !!this.currentImage();
        if (this.zoomOutBtn) this.zoomOutBtn.disabled = !isImage || this.zoom <= this.MIN_ZOOM;
        if (this.zoomInBtn) this.zoomInBtn.disabled = !isImage || this.zoom >= this.MAX_ZOOM;
      }

      clampPan() {
        const img = this.currentImage();
        if (!img || this.zoom <= 1) {
          this.panX = 0;
          this.panY = 0;
          return;
        }
        const vp = this.viewport.getBoundingClientRect();
        // base (unscaled) rendered size
        const baseW = img.clientWidth;
        const baseH = img.clientHeight;
        const maxX = Math.max(0, (baseW * this.zoom - vp.width) / 2);
        const maxY = Math.max(0, (baseH * this.zoom - vp.height) / 2);
        this.panX = Math.max(-maxX, Math.min(this.panX, maxX));
        this.panY = Math.max(-maxY, Math.min(this.panY, maxY));
      }

      applyTransform() {
        const img = this.currentImage();
        if (!img) return;
        this.clampPan();
        img.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
        img.classList.toggle('is-zoomed', this.zoom > 1);
      }

      /* ----- events ----- */

      bindEvents() {
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());
        if (this.zoomInBtn) this.zoomInBtn.addEventListener('click', () => this.zoomIn());
        if (this.zoomOutBtn) this.zoomOutBtn.addEventListener('click', () => this.zoomOut());

        // Keyboard: arrows change slide, +/- zoom (Esc handled by base ModalDialog)
        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;
          switch (event.key) {
            case 'ArrowRight':
              event.preventDefault();
              this.next();
              break;
            case 'ArrowLeft':
              event.preventDefault();
              this.prev();
              break;
            case '+':
            case '=':
              event.preventDefault();
              this.zoomIn();
              break;
            case '-':
            case '_':
              event.preventDefault();
              this.zoomOut();
              break;
          }
        });

        // Desktop double-click toggles zoom on the image
        this.viewport.addEventListener('dblclick', (event) => {
          if (!this.currentImage()) return;
          event.preventDefault();
          if (this.zoom > 1) this.setZoom(this.MIN_ZOOM);
          else this.setZoom(2);
        });

        // Wheel to zoom on the image
        this.viewport.addEventListener(
          'wheel',
          (event) => {
            if (!this.currentImage()) return;
            event.preventDefault();
            if (event.deltaY < 0) this.zoomIn();
            else this.zoomOut();
          },
          { passive: false }
        );

        // Pointer gestures: swipe between slides, drag to pan, pinch to zoom
        this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.viewport.addEventListener('pointercancel', this.onPointerUp.bind(this));

        // Click the empty area around the media (not a drag) to close
        this.viewport.addEventListener('click', (event) => {
          if (this.movedDuringGesture) return;
          const onMedia = event.target.closest(
            'img, video, iframe, model-viewer, .deferred-media, product-model, button'
          );
          if (!onMedia) this.hide();
        });

        // Re-clamp pan if the viewport resizes while zoomed
        window.addEventListener('resize', () => {
          if (this.hasAttribute('open') && this.zoom > 1) this.applyTransform();
        });
      }

      onPointerDown(event) {
        this.movedDuringGesture = false;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 2) {
          // start pinch
          this.dragging = false;
          this.swiping = false;
          this.pinchStartDist = this.pointerDistance();
          this.pinchStartZoom = this.zoom;
          return;
        }

        this.startX = event.clientX;
        this.startY = event.clientY;

        if (this.zoom > 1 && this.currentImage()) {
          // pan
          this.dragging = true;
          this.startPanX = this.panX;
          this.startPanY = this.panY;
          const img = this.currentImage();
          if (img) img.classList.add('is-grabbing');
          this.viewport.setPointerCapture(event.pointerId);
        } else {
          // potential swipe
          this.swiping = true;
        }
      }

      onPointerMove(event) {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (
          !this.movedDuringGesture &&
          (Math.abs(event.clientX - this.startX) > 5 || Math.abs(event.clientY - this.startY) > 5)
        ) {
          this.movedDuringGesture = true;
        }

        if (this.pointers.size === 2 && this.pinchStartDist > 0) {
          const ratio = this.pointerDistance() / this.pinchStartDist;
          this.setZoom(this.pinchStartZoom * ratio);
          return;
        }

        if (this.dragging) {
          this.panX = this.startPanX + (event.clientX - this.startX);
          this.panY = this.startPanY + (event.clientY - this.startY);
          this.applyTransform();
        } else if (this.swiping) {
          // follow finger a little for feedback
          const dx = event.clientX - this.startX;
          this.track.style.transition = 'none';
          this.track.style.transform = `translateX(calc(${-this.index * 100}% + ${dx}px))`;
        }
      }

      onPointerUp(event) {
        const wasSwiping = this.swiping;
        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;

        this.pointers.delete(event.pointerId);

        const img = this.currentImage();
        if (img) img.classList.remove('is-grabbing');

        if (this.pointers.size < 2) {
          this.pinchStartDist = 0;
        }

        if (this.dragging) {
          this.dragging = false;
          return;
        }

        if (wasSwiping) {
          this.swiping = false;
          this.track.style.transition = '';
          const threshold = Math.min(80, this.viewport.clientWidth * 0.15);
          if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
            if (dx < 0) this.next();
            else this.prev();
          } else {
            // snap back
            this.goTo(this.index);
          }
        }
      }

      pointerDistance() {
        const pts = Array.from(this.pointers.values());
        if (pts.length < 2) return 0;
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        return Math.hypot(dx, dy);
      }
    }
  );
}
